import * as SheetPrimitive from "@radix-ui/react-dialog";
import { cva, type VariantProps } from "class-variance-authority";
import { X } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import * as React from "react";

import { cn } from "@/core/utils";

type SheetContextValue = {
  open: boolean;
  modal: boolean;
};

const SheetContext = React.createContext<SheetContextValue | null>(null);

function useSheetContext() {
  const ctx = React.useContext(SheetContext);
  if (!ctx) {
    throw new Error("Sheet components must be used within <Sheet>.");
  }
  return ctx;
}

type SheetRootProps = React.ComponentPropsWithoutRef<typeof SheetPrimitive.Root>;

const Sheet = ({
  open: openProp,
  defaultOpen,
  onOpenChange,
  modal = true,
  ...props
}: SheetRootProps) => {
  const isControlled = openProp !== undefined;
  const [uncontrolledOpen, setUncontrolledOpen] = React.useState(defaultOpen ?? false);

  const open = isControlled ? (openProp as boolean) : uncontrolledOpen;

  const handleOpenChange = React.useCallback(
    (nextOpen: boolean) => {
      if (!isControlled) setUncontrolledOpen(nextOpen);
      onOpenChange?.(nextOpen);
    },
    [isControlled, onOpenChange],
  );

  return (
    <SheetContext.Provider value={{ open, modal }}>
      <SheetPrimitive.Root modal={modal} open={open} onOpenChange={handleOpenChange} {...props} />
    </SheetContext.Provider>
  );
};

const SheetTrigger = SheetPrimitive.Trigger;

const SheetClose = SheetPrimitive.Close;

const SheetPortal = SheetPrimitive.Portal;

const SheetOverlay = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Overlay asChild {...props}>
    <motion.div
      animate={{ opacity: 1 }}
      className={cn("fixed inset-0 z-50 bg-black/80", className)}
      exit={{ opacity: 0 }}
      initial={{ opacity: 0 }}
      ref={ref}
      transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
    />
  </SheetPrimitive.Overlay>
));
SheetOverlay.displayName = SheetPrimitive.Overlay.displayName;

const sheetVariants = cva("bg-background fixed z-50 gap-4 p-6 shadow-lg", {
  variants: {
    side: {
      top: "inset-x-0 top-0 border-b",
      bottom: "inset-x-0 bottom-0 border-t",
      left: "inset-y-0 left-0 h-full w-3/4 border-r sm:max-w-sm",
      right: "inset-y-0 right-0 h-full w-3/4 border-l sm:max-w-sm",
      fade: "inset-0 h-dvh w-screen border-0",
    },
  },
  defaultVariants: {
    side: "right",
  },
});

interface SheetContentProps
  extends
    React.ComponentPropsWithoutRef<typeof SheetPrimitive.Content>,
    VariantProps<typeof sheetVariants> {}

function containsDialogTitle(node: React.ReactNode): boolean {
  let found = false;

  React.Children.forEach(node, (child) => {
    if (found) return;
    if (!React.isValidElement<{ children?: React.ReactNode }>(child)) return;

    if (child.type === SheetTitle || child.type === SheetPrimitive.Title) {
      found = true;
      return;
    }

    if (child.props.children !== undefined) {
      found = containsDialogTitle(child.props.children);
    }
  });

  return found;
}

function getContentMotion(side: NonNullable<SheetContentProps["side"]> | null) {
  if (!side) {
    return {
      initial: { opacity: 0 },
      animate: { opacity: 1 },
      exit: { opacity: 0 },
    };
  }
  switch (side) {
    case "fade":
      return {
        initial: { opacity: 0, scale: 0.99 },
        animate: { opacity: 1, scale: 1 },
        exit: { opacity: 0, scale: 0.99 },
      };
    case "left":
      return {
        initial: { x: "-100%" },
        animate: { x: 0 },
        exit: { x: "-100%" },
      };
    case "right":
      return {
        initial: { x: "100%" },
        animate: { x: 0 },
        exit: { x: "100%" },
      };
    case "top":
      return {
        initial: { y: "-100%" },
        animate: { y: 0 },
        exit: { y: "-100%" },
      };
    case "bottom":
      return {
        initial: { y: "100%" },
        animate: { y: 0 },
        exit: { y: "100%" },
      };
  }
}

const SheetContent = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Content>,
  SheetContentProps
>(({ side = "right", className, children, ...props }, ref) => (
  <SheetPortal>
    <SheetMotionContent className={className} ref={ref} side={side} {...props}>
      {children}
    </SheetMotionContent>
  </SheetPortal>
));
SheetContent.displayName = SheetPrimitive.Content.displayName;

const SheetMotionContent = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Content>,
  SheetContentProps
>(({ side = "right", className, children, ...props }, ref) => {
  const { open, modal } = useSheetContext();
  const motionProps = getContentMotion(side);
  const hasTitle = containsDialogTitle(children);

  return (
    <AnimatePresence>
      {open ? (
        <React.Fragment>
          {modal ? <SheetOverlay key="sheet-overlay" /> : null}
          <SheetPrimitive.Content asChild key="sheet-content" {...props}>
            <motion.div
              animate={motionProps.animate}
              className={cn(sheetVariants({ side }), className)}
              exit={motionProps.exit}
              initial={motionProps.initial}
              ref={ref}
              transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
            >
              {!hasTitle ? (
                <SheetPrimitive.Title className="sr-only">Dialog</SheetPrimitive.Title>
              ) : null}
              {children}
              <SheetPrimitive.Close className="ring-offset-background data-[state=open]:bg-secondary focus:ring-ring absolute top-4 right-4 rounded-sm opacity-70 transition-opacity hover:opacity-100 focus:ring-2 focus:ring-offset-2 focus:outline-none disabled:pointer-events-none">
                <X className="h-4 w-4" />
                <span className="sr-only">Close</span>
              </SheetPrimitive.Close>
            </motion.div>
          </SheetPrimitive.Content>
        </React.Fragment>
      ) : null}
    </AnimatePresence>
  );
});
SheetMotionContent.displayName = "SheetMotionContent";

const SheetHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn("flex flex-col space-y-2 text-center sm:text-left", className)} {...props} />
);
SheetHeader.displayName = "SheetHeader";

const SheetFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn("flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2", className)}
    {...props}
  />
);
SheetFooter.displayName = "SheetFooter";

const SheetTitle = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Title>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Title
    className={cn("text-foreground text-lg font-semibold", className)}
    ref={ref}
    {...props}
  />
));
SheetTitle.displayName = SheetPrimitive.Title.displayName;

const SheetDescription = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Description>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Description
    className={cn("text-muted-foreground text-sm", className)}
    ref={ref}
    {...props}
  />
));
SheetDescription.displayName = SheetPrimitive.Description.displayName;

export {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetOverlay,
  SheetPortal,
  SheetTitle,
  SheetTrigger,
};
